import { Controller } from "@hotwired/stimulus";

/**
 * Handles compose message form interactions
 * - Customer selection dropdown with search
 * - Toggle between existing customer and new recipient
 * - Phone number radio button styling
 */
export default class extends Controller {
  static targets = [
    "customerId",
    "dropdownContainer",
    "selectedName",
    "customerList",
    "searchInput",
    "customerItem",
    "phoneInputSection",
    "emailInputSection",
    "nameSection"
  ];

  static values = {
    channel: String,
    noPhoneLabel: String,
    noEmailLabel: String,
    newPhoneNumberLabel: String,
    newEmailAddressLabel: String
  };

  connect() {
    // Close dropdown when clicking outside
    this.boundCloseDropdown = this.closeDropdownOnOutsideClick.bind(this);
    document.addEventListener("click", this.boundCloseDropdown);
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseDropdown);
  }

  toggleDropdown(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.hasCustomerListTarget) {
      this.customerListTarget.classList.toggle("hidden");

      if (!this.customerListTarget.classList.contains("hidden") && this.hasSearchInputTarget) {
        this.searchInputTarget.focus();
      }
    }
  }

  closeDropdownOnOutsideClick(event) {
    if (!this.hasDropdownContainerTarget) return;

    if (!this.dropdownContainerTarget.contains(event.target)) {
      this.closeDropdown();
    }
  }

  closeDropdown() {
    if (this.hasCustomerListTarget) {
      this.customerListTarget.classList.add("hidden");
    }
  }

  selectCustomer(event) {
    const item = event.currentTarget;
    const customerId = item.dataset.id;
    const customerName = item.dataset.name;
    const customerPhone = item.dataset.phone;
    const customerEmail = item.dataset.email;

    // Set hidden customer ID
    if (this.hasCustomerIdTarget) {
      this.customerIdTarget.value = customerId;
    }

    // Update selected name display
    if (this.hasSelectedNameTarget) {
      const displayText = this.channelValue === "sms"
        ? `${customerName} (${customerPhone || this.noPhoneLabelValue})`
        : `${customerName} (${customerEmail || this.noEmailLabelValue})`;
      this.selectedNameTarget.textContent = displayText;
      this.selectedNameTarget.classList.remove("text-gray-500", "dark:text-gray-400");
      this.selectedNameTarget.classList.add("text-gray-900", "dark:text-gray-100");
    }

    // Hide manual input sections
    this.hideManualInputSections();

    this.closeDropdown();
  }

  selectNewRecipient(event) {
    event.preventDefault();

    // Clear customer ID
    if (this.hasCustomerIdTarget) {
      this.customerIdTarget.value = "";
    }

    // Update selected name display
    if (this.hasSelectedNameTarget) {
      const placeholder = this.channelValue === "sms"
        ? this.newPhoneNumberLabelValue
        : this.newEmailAddressLabelValue;
      this.selectedNameTarget.textContent = placeholder;
      this.selectedNameTarget.classList.remove("text-gray-900", "dark:text-gray-100");
      this.selectedNameTarget.classList.add("text-gray-500", "dark:text-gray-400");
    }

    // Show manual input sections
    this.showManualInputSections();

    this.closeDropdown();
  }

  hideManualInputSections() {
    if (this.hasPhoneInputSectionTarget) {
      this.phoneInputSectionTarget.classList.add("hidden");
    }
    if (this.hasEmailInputSectionTarget) {
      this.emailInputSectionTarget.classList.add("hidden");
    }
    if (this.hasNameSectionTarget) {
      this.nameSectionTarget.classList.add("hidden");
    }
  }

  showManualInputSections() {
    if (this.channelValue === "sms" && this.hasPhoneInputSectionTarget) {
      this.phoneInputSectionTarget.classList.remove("hidden");
    }
    if (this.channelValue === "email" && this.hasEmailInputSectionTarget) {
      this.emailInputSectionTarget.classList.remove("hidden");
    }
    if (this.hasNameSectionTarget) {
      this.nameSectionTarget.classList.remove("hidden");
    }
  }

  filterCustomers(event) {
    const query = event.target.value.toLowerCase().trim();

    this.customerItemTargets.forEach((item) => {
      // Skip the "new recipient" option
      if (item.dataset.action?.includes("selectNewRecipient")) {
        return;
      }

      const name = (item.dataset.name || "").toLowerCase();
      const phone = (item.dataset.phone || "").toLowerCase();
      const email = (item.dataset.email || "").toLowerCase();

      const matches = name.includes(query) ||
                      phone.includes(query) ||
                      email.includes(query);

      item.classList.toggle("hidden", !matches);
    });
  }

  updateRadioStyles(event) {
    const selectedRadio = event.target;
    const selectedLabel = selectedRadio.closest("label");

    // Get all radio labels in this group
    const form = selectedRadio.closest("form");
    const allLabels = form.querySelectorAll(`label:has(input[name="${selectedRadio.name}"])`);

    allLabels.forEach((label) => {
      const isSelected = label === selectedLabel;

      // Remove all state classes
      label.classList.remove(
        "border-indigo-500", "bg-indigo-50", "dark:bg-indigo-900/20", "dark:border-indigo-500",
        "border-gray-200", "dark:border-gray-700"
      );

      if (isSelected) {
        label.classList.add("border-indigo-500", "bg-indigo-50", "dark:bg-indigo-900/20", "dark:border-indigo-500");
      } else {
        label.classList.add("border-gray-200", "dark:border-gray-700");
      }
    });
  }
}
